import React, { useEffect, useState } from "react";
import Modal from "../../../components/Atomos/Modals/Modal";
import Button from "../../../components/staff/Button";
import EmailTemplatePreview from "../../../components/Html/EmailTemplatePreview";
import { getEmailTemplatesIncludeDeleted } from "../../../Services/EmailTemplate";
import type { IEmailTemplate } from "../../../interfaces/models/IEmailTemplate";

const PREVIEW_FIELDS = ["template", "isDeleted"];

type EmailStatsTemplatePreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templateId?: string;
  emailNumber?: string | number;
};

function preferActiveTemplate (
  rows: IEmailTemplate[],
): IEmailTemplate | undefined {
  return rows.find((row) => row.isDeleted !== true) ?? rows[0];
}

async function findTemplateById (templateId: string): Promise<IEmailTemplate | null> {
  const id = templateId.trim();
  if (!id || /^\d+$/.test(id)) {
    return null;
  }
  const rows = await getEmailTemplatesIncludeDeleted({
    filter: { id: { value: id, comparisonType: "EQUAL" } },
    fields: PREVIEW_FIELDS,
    pagination: { pageIndex: 0, pageSize: 5 },
  });
  return preferActiveTemplate(rows) ?? null;
}

async function findTemplateByEmailNumber (
  emailNumber: string | number,
): Promise<IEmailTemplate | null> {
  const numeric = Number(emailNumber);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const byNumber = await getEmailTemplatesIncludeDeleted({
    filter: { emailNumber: { value: numeric, comparisonType: "EQUAL" } },
    fields: PREVIEW_FIELDS,
    pagination: { pageIndex: 0, pageSize: 5 },
  });
  const numericMatch = preferActiveTemplate(byNumber);
  if (numericMatch) {
    return numericMatch;
  }
  const byNumberText = await getEmailTemplatesIncludeDeleted({
    filter: {
      emailNumber: { value: String(numeric), comparisonType: "EQUAL" },
    },
    fields: PREVIEW_FIELDS,
    pagination: { pageIndex: 0, pageSize: 5 },
  });
  return preferActiveTemplate(byNumberText) ?? null;
}

export default function EmailStatsTemplatePreviewModal ({
  isOpen,
  onClose,
  templateId,
  emailNumber,
}: EmailStatsTemplatePreviewModalProps): React.ReactElement {
  const [status, setStatus] = useState<"loading" | "ready" | "missing">("loading");
  const [html, setHtml] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setStatus("loading");
      setHtml("");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setHtml("");

    const load = async () => {
      const byId = await findTemplateById(templateId ?? "");
      if (byId) {
        return byId;
      }
      if (emailNumber == null) {
        return null;
      }
      return findTemplateByEmailNumber(emailNumber);
    };

    load()
      .then((template) => {
        if (cancelled) {
          return;
        }
        if (!template) {
          setStatus("missing");
          return;
        }
        setHtml(template.template || "");
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("missing");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, templateId, emailNumber]);

  const title =
    emailNumber != null ? `Template ${emailNumber}` : "Template preview";

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title={title}
      titleColor="text-gray-font"
    >
      {status === "loading" ? (
        <p className="text-base text-gray-font">Loading preview...</p>
      ) : null}
      {status === "missing" ? (
        <p className="text-base text-gray-font">Template not found.</p>
      ) : null}
      {status === "ready" ? (
        <EmailTemplatePreview html={html} minHeight={320} />
      ) : null}
      <div className="flex justify-end mt-4">
        <Button
          text="Close"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={onClose}
          className="!w-auto"
        />
      </div>
    </Modal>
  );
}
